/**
 * The software library's file-name and exit-code rules.
 *
 * A hand port of the static helpers on `Contracts/SoftwareDeployment.cs` `SoftwareDeploymentLimits`,
 * which `ContractsGen` cannot carry: they are behavior, and `AllowedExtensions` is a `static readonly`
 * array rather than a const. The server re-applies every one of these on save, so the form only has to
 * agree with it - which `software.test.ts` pins.
 */

/** What the agent knows how to launch. An allowlist: anything it cannot identify is refused. */
export const ALLOWED_EXTENSIONS: readonly string[] = ['.msi', '.msp', '.msu', '.exe', '.cmd', '.bat', '.ps1'];

/** 0, plus the two "succeeded, a restart is pending" codes Windows Installer uses. */
export const DEFAULT_SUCCESS_EXIT_CODES: readonly number[] = [0, 1641, 3010];

const MAX_FILE_NAME_LENGTH = 160;

/** `.msi`, lower-cased, or '' for a name with no usable extension. */
export function extensionOf(fileName: string | null | undefined): string {
  const name = (fileName ?? '').trim();
  if (!name) return '';
  const dot = name.lastIndexOf('.');
  return dot < 0 || dot === name.length - 1 ? '' : name.slice(dot).toLowerCase();
}

export function isAllowedExtension(fileName: string | null | undefined): boolean {
  const ext = extensionOf(fileName);
  return ext.length > 0 && ALLOWED_EXTENSIONS.includes(ext);
}

/** A bare file name with no directory parts and nothing a Windows file name may not hold. */
export function safeFileName(name: string | null | undefined): string {
  let raw = (name ?? '').trim();
  const slash = Math.max(raw.lastIndexOf('/'), raw.lastIndexOf('\\'));
  if (slash >= 0) raw = raw.slice(slash + 1);
  // eslint-disable-next-line no-control-regex
  const clean = raw.replace(/[\u0000-\u001f\u007f<>:"|?*]/g, '').trim();
  if (clean.length === 0 || /^\.+$/.test(clean)) return 'installer.exe';
  return clean.length > MAX_FILE_NAME_LENGTH ? clean.slice(clean.length - MAX_FILE_NAME_LENGTH) : clean;
}

/**
 * The file name a download URL implies, for pre-filling the form - or '' when it would not be an
 * installer the agent can launch, so the form asks rather than proposing something save will refuse.
 */
export function fileNameFromUrl(url: string | null | undefined): string {
  let text = (url ?? '').trim();
  if (!text) return '';
  const cut = text.search(/[?#]/);
  if (cut >= 0) text = text.slice(0, cut);
  const name = text.slice(text.lastIndexOf('/') + 1);
  return isAllowedExtension(name) ? safeFileName(name) : '';
}

/** "0, 1641, 3010" - how the form shows a package's success codes, and what it sends back. */
export function describeExitCodes(codes: readonly number[] | null | undefined): string {
  return (codes && codes.length > 0 ? codes : DEFAULT_SUCCESS_EXIT_CODES).join(', ');
}
